import clsx from 'clsx'
import compact from 'lodash/compact'
import first from 'lodash/first'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import React, { useMemo } from 'react'

import { BlockContainer } from '@Components/BlockContainer'
import { ImageProduct } from '@Components/CMS/ProductGrid'
import { Settings, Video, VideoProps } from '@Components/CMS/Video'
import { VimeoPlayer } from '@Components/CMS/Video/VimeoPlayer'
import { Block, Product, VideoResource } from '@Generated/graphql'
import { BlockContext } from '@Globals/CMS/Context'
import { getJSONFieldValue, getObjectFieldValue, getPageReferences, getProductFieldValue, getReferenceFieldValue } from '@Globals/CMS/FieldHelpers'

import styles from './VideoBlock.module.scss'

const videoWidthToVideoType = (type: string): 'column' | 'full-screen' | 'full' => {
  switch (type) {
    case 'Column':
      return 'column'
    case 'Full screen':
      return 'full-screen'
    case 'Full page':
      return 'full'
    default:
      return 'column'
  }
}

const videoHeightToMaxHeight = (height: string): string => {
  if (!height || height === 'Auto') {
    return 'auto'
  }

  return height.substring(0, height.length - 1)
}

const productToImageProduct = (product: Product): ImageProduct => ({
  id: product.id,
  link: product.url || '',
  title: product.name || '',
})

type DesktopCropping = 'top' | 'center' | 'bottom'

interface VideoBlockData {
  caption: string
  desktopCropping: DesktopCropping
  desktopMaxHeight: string
  products: any[]
  settings?: Settings
  type: 'column' | 'full-screen' | 'full'
  videoId: string
  videoMobileId: string
  videoType: VideoProps['videoType']
}

const getBlockData = (videoBlock: Block): VideoBlockData => {
  const settings = getObjectFieldValue(videoBlock.fields || [], 'settings') || []
  const videoResource = first(getReferenceFieldValue<VideoResource>(videoBlock.fields || [], 'video'))
  const captionOptions = videoResource?.captionOptions
  const captionPageReferences = getPageReferences(captionOptions?.references || [])

  const products = compact(map(captionPageReferences, (x) => {
    const product = getProductFieldValue(x.fields || [], 'product')
    if (!product) {
      return undefined
    }
    return productToImageProduct(product)
  }))

  return {
    caption: captionOptions?.caption || '',
    desktopCropping: getJSONFieldValue<DesktopCropping>(settings, 'cropping')?.toLowerCase() as Lowercase<DesktopCropping>,
    desktopMaxHeight: videoHeightToMaxHeight(getJSONFieldValue(settings, 'height') || ''),
    products: products || [],
    type: videoWidthToVideoType(getJSONFieldValue(settings, 'width') || ''),
    videoId: videoResource?.asset?.key || '',
    videoMobileId: videoResource?.mobileAsset?.key || '',
    videoType: videoResource?.asset?.type as VideoProps['videoType'],
    settings: { ...(videoResource?.settings as Settings) },
  }
}

const VideoBlock = ({
  blockIndex,
  data,
  id,
}: {
  blockIndex: number
  data: Block
  id?: string
}) => {
  const { isInjection } = React.use(BlockContext)
  const {
    caption,
    desktopCropping,
    desktopMaxHeight,
    products,
    settings,
    type,
    videoId,
    videoMobileId,
    videoType,
  } = useMemo(() => getBlockData(data), [data])
  const { appearance, common } = data
  if (!videoType) {
    return null
  }

  if (isInjection) {
    return (
      <div className={clsx(styles.background)}>
        <VimeoPlayer
          className={styles.video}
          enableControls={false}
          options={{
            autoplay: true,
            muted: true,
            loop: true,
          }}
          videoID={videoId}
        />
      </div>
    )
  }

  if (!isEmpty(common?.title)) {
    return (
      <BlockContainer
        appearance={appearance || {}}
        common={common || {}}
        noPadding={type === 'full-screen'}
      >
        <Video
          blockIndex={blockIndex}
          caption={caption}
          className={clsx(type === 'full-screen' && styles.fullScreenVideo)}
          desktopCropping={desktopCropping}
          desktopMaxHeight={desktopMaxHeight}
          id={id}
          isInjection={isInjection}
          products={products}
          settings={settings}
          type={type}
          videoId={videoId}
          videoMobileId={videoMobileId}
          videoType={videoType}
        />
      </BlockContainer>
    )
  }

  return (
    <Video
      blockIndex={blockIndex}
      caption={caption}
      desktopCropping={desktopCropping}
      desktopMaxHeight={desktopMaxHeight}
      id={id}
      isInjection={isInjection}
      products={products}
      settings={settings}
      type={type}
      videoId={videoId}
      videoMobileId={videoMobileId}
      videoType={videoType}
      withMargin
    />
  )
}

export default VideoBlock
